// NWS WPC Surface Analysis layer family.
//
// Single transparent PNG over a fixed CONUS bounding box. We use an
// 'image' source (one georeferenced raster) instead of a tile service
// — WPC publishes a whole-CONUS chart, not z/x/y tiles. Coordinates
// pulled from the WPC KML wrapper's LatLonBox.
//
// Extracted from addOverlays.ts as HI-1 batch 6b (audit 2026-05-22).
// Pure mechanical move — image-source coordinates, paint opacity, and
// layer insertion order are byte-identical to the pre-extraction form.

import type { Map as MlMap } from 'maplibre-gl';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useUiStore } from '../../stores/uiStore';

/**
 * Mount the surface-analysis image source + raster layer. Idempotent
 * on the source.
 */
export function addSfcAnalysisLayers(map: MlMap): void {
  if (!map.getSource('sfcanalysis')) {
    map.addSource('sfcanalysis', {
      type: 'image',
      url: '/api/wx/sfcanalysis',
      // Coordinates: TL, TR, BR, BL in [lon, lat] order. Pulled from
      // the WPC KML wrapper's LatLonBox.
      coordinates: [
        [-130.08, 58.79], // top-left
        [ -64.06, 58.79], // top-right
        [ -64.06, 14.88], // bottom-right
        [-130.08, 14.88], // bottom-left
      ],
    } as any);
    const beforeId = (() => {
      // Sit beneath the airspace polygons but above NEXRAD.
      for (const id of ['sua-fill', 'tfr-fill', 'fires-fill']) {
        if (map.getLayer(id)) return id;
      }
      return undefined;
    })();
    map.addLayer(
      {
        id: 'sfcanalysis-raster',
        type: 'raster',
        source: 'sfcanalysis',
        layout: { visibility: 'none' },
        paint: {
          'raster-opacity': 0.85,
        },
      },
      beforeId,
    );
  }
}

registerLayerFamily({
  id: 'sfcanalysis',
  label: 'WPC surface analysis',
  sourceId: 'sfcanalysis',
  layerIds: ['sfcanalysis-raster'],
  mount: (map) => addSfcAnalysisLayers(map),
  visibilitySelector: (l) => l.sfcanalysis,
  // applyState: visibility + image URL (time-scrubber) in one call.
  applyState: (map) => {
    if (!map.getLayer('sfcanalysis-raster')) return;
    const show = useLayerVisibilityStore.getState().layers.sfcanalysis;
    map.setLayoutProperty('sfcanalysis-raster', 'visibility', show ? 'visible' : 'none');
    if (show) {
      const sfcAnalysisOffsetH = useUiStore.getState().sfcAnalysisOffsetH;
      const src = map.getSource('sfcanalysis') as { updateImage?: (opts: { url: string }) => void } | undefined;
      src?.updateImage?.({ url: `/api/wx/sfcanalysis?hours=${sfcAnalysisOffsetH}` });
    }
  },
});
